import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import ShiftEntries from "@/pages/ShiftEntries";
import ShiftEntryForm from "@/pages/ShiftEntryForm";
import ShiftEntryDetail from "@/pages/ShiftEntryDetail";
import UploadInbox from "@/pages/UploadInbox";
import Reconciliation from "@/pages/Reconciliation";
import Flags from "@/pages/Flags";
import FraudDashboard from "@/pages/FraudDashboard";
import ManagerDaily from "@/pages/ManagerDaily";
import FuelPurchase from "@/pages/FuelPurchase";
import Lubricants from "@/pages/Lubricants";
import DebtorLedger from "@/pages/DebtorLedger";
import SalesSummary from "@/pages/SalesSummary";
import Masters from "@/pages/Masters";
import Users from "@/pages/Users";
import Config from "@/pages/Config";
import Integrations from "@/pages/Integrations";
import ManagerQueue from "@/pages/ManagerQueue";
import LubricantLabels from "@/pages/LubricantLabels";
import Invites from "@/pages/Invites";
import Attendance from "@/pages/Attendance";
import DipToDip from "@/pages/DipToDip";
import FillerWizard from "@/pages/FillerWizard";
import DrillThrough from "@/pages/DrillThrough";
import Tour from "@/pages/Tour";
import RoleHome from "@/pages/RoleHome";
import InterestCalc from "@/pages/InterestCalc";
import LegacyExport from "@/pages/LegacyExport";
import AdminWipe from "@/pages/AdminWipe";
import Payroll from "@/pages/Payroll";
import EmployeeRequests from "@/pages/EmployeeRequests";
import DebtorsPro from "@/pages/DebtorsPro";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-slate-600">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RoleAwareHome() {
  const { user } = useAuth();
  // Fillers get taken straight into the Punch Shift wizard — that's their entire job.
  if (user?.role === "filler") return <Navigate to="/filler" replace />;
  return <RoleHome />;
}

function AppRouter() {
  const location = useLocation();
  // Handle session_id from URL fragment (Emergent Auth callback)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<RoleAwareHome />} />
        <Route path="analytics" element={<Dashboard />} />
        <Route path="tour" element={<Tour />} />
        <Route path="shifts" element={<ShiftEntries />} />
        <Route path="shifts/new" element={<ShiftEntryForm />} />
        <Route path="shifts/:entryId" element={<ShiftEntryDetail />} />
        <Route path="shifts/:entryId/edit" element={<ShiftEntryForm />} />
        <Route path="uploads" element={<UploadInbox />} />
        <Route path="reconciliation" element={<Reconciliation />} />
        <Route path="flags" element={<Flags />} />
        <Route path="fraud" element={<FraudDashboard />} />
        <Route path="manager-daily" element={<ManagerDaily />} />
        <Route path="fuel-purchase" element={<FuelPurchase />} />
        <Route path="lubricants" element={<Lubricants />} />
        <Route path="debtors" element={<DebtorLedger />} />
        <Route path="sales" element={<SalesSummary />} />
        <Route path="masters" element={<Masters />} />
        <Route path="users" element={<Users />} />
        <Route path="config" element={<Config />} />
        <Route path="integrations" element={<Integrations />} />
        <Route path="review-queue" element={<ManagerQueue />} />
        <Route path="lubricant-labels" element={<LubricantLabels />} />
        <Route path="invites" element={<Invites />} />
        <Route path="attendance" element={<Attendance />} />
        <Route path="dip-to-dip" element={<DipToDip />} />
        <Route path="filler" element={<FillerWizard />} />
        <Route path="drill/:kpi" element={<DrillThrough />} />
        <Route path="interest" element={<InterestCalc />} />
        <Route path="legacy-export" element={<LegacyExport />} />
        <Route path="wipe" element={<AdminWipe />} />
        <Route path="payroll" element={<Payroll />} />
        <Route path="employee-requests" element={<EmployeeRequests />} />
        <Route path="debtors-pro" element={<DebtorsPro />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}
